import React from "react";
import ZoomableImage from "components/ZoomableImage";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

type FigureLegend = Record<string, string>;

export interface FeatureColumnData {
  /** annotation_id when available, else "<m|n>-<bucketIndex>" */
  key: string;
  subFeature: string;
  isMatched: boolean;
  isCurrent: boolean;
  hexCode?: string;
  patentFigureUrl?: string;
  figureNumber?: string;
  figureLegend?: FigureLegend[];
  accusedImageUrl?: string;
  /** Single-feature claim-chart table (pre-built HTML). */
  chartHtml?: string;
  /** DSPy text analysis (citations [n] converted to sups by the parent walker). */
  analysisHtml?: string;
  figureInsightHtml?: string;
  matchPercentage?: string | number;
}

interface PerFeatureAnalysisViewProps {
  claimLabel: string;
  clauseHtml: string;
  /** One table for the whole element — each feature's row from its own item. */
  commonChartHtml?: string;
  /** Analysis text of the item at hand only. */
  currentAnalysisHtml?: string;
  currentSubFeature?: string;
  columns: FeatureColumnData[];
  /** Key of the column expanded to the 80% modal (null = none). */
  expandedKey?: string | null;
  onToggleExpand?: (key: string | null) => void;
  onSelectColumn: (key: string) => void;
  onChangePatentImage: (key: string) => void;
  onAnnotate: (key: string) => void;
  onReanalyze: (key: string) => void;
  /** Source patent document link ("View Patent" beside the figure header). */
  patentLink?: string;
  /** Target document link ("View Target" beside the accused header). */
  targetLink?: string;
}

const CameraIcon = ({ size = 40, stroke = "#98A2B3" }: { size?: number; stroke?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
    <circle cx="8.5" cy="8.5" r="1.5"></circle>
    <polyline points="21 15 16 10 5 21"></polyline>
  </svg>
);

const PencilIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const RefreshIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"></polyline>
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
  </svg>
);

const NoPreview = ({ height = "14rem" }: { height?: string }) => (
  <div
    className="w-full bg-[#F2F4F7] border-2 border-dashed border-[#D0D5DD] rounded-lg flex flex-col items-center justify-center gap-2.5"
    style={{ height }}
  >
    <CameraIcon />
    <span className="text-sm text-[#667085] font-interMedium">No Preview Available</span>
  </div>
);

const actionBtnCls =
  "flex items-center gap-1 px-2.5 py-1 text-xs text-white rounded";
const actionBtnStyle: React.CSSProperties = {
  backgroundColor: "#1751D0",
  border: "none",
  cursor: "pointer",
};

/**
 * Per-feature "View More Details": one column per sub-feature of the clicked
 * item's element clause. The current item's column shows its own analysis;
 * sibling columns are assembled from the siblings' own stored items (own
 * patent figure x own accused image x own verdict) — nothing is recomputed.
 */
const PerFeatureAnalysisView: React.FC<PerFeatureAnalysisViewProps> = ({
  claimLabel,
  clauseHtml,
  commonChartHtml,
  currentAnalysisHtml,
  currentSubFeature,
  columns,
  expandedKey,
  onToggleExpand,
  onSelectColumn,
  onChangePatentImage,
  onAnnotate,
  onReanalyze,
  patentLink,
  targetLink,
}) => {
  // Small "View Patent" / "View Target" link rendered beside the section
  // captions; the target may be a website, a file, or another patent, so the
  // labels stay format-agnostic.
  const sourceLink = (href: string | undefined, label: string) =>
    href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-0.5 text-[10px] font-interSemiBold text-[#1751D0] hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {label}
        <OpenInNewIcon sx={{ fontSize: 11 }} />
      </a>
    ) : null;
  const n = columns.length;

  // Columns render as a single horizontal strip. When they overflow the
  // viewport the strip scrolls (snap + chevron paddles + edge fades); when
  // they fit it just centers them.
  const stripRef = React.useRef<HTMLDivElement | null>(null);
  const [strip, setStrip] = React.useState({
    overflowing: false,
    canLeft: false,
    canRight: false,
  });

  const updateStrip = React.useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    const overflowing = el.scrollWidth > el.clientWidth + 4;
    setStrip((prev) => {
      const next = {
        overflowing,
        canLeft: overflowing && el.scrollLeft > 4,
        canRight:
          overflowing && el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
      };
      return prev.overflowing === next.overflowing &&
        prev.canLeft === next.canLeft &&
        prev.canRight === next.canRight
        ? prev
        : next;
    });
  }, []);

  React.useEffect(() => {
    updateStrip();
    window.addEventListener("resize", updateStrip);
    return () => window.removeEventListener("resize", updateStrip);
  }, [updateStrip, columns]);

  // Keep the selected feature's column in view (pill clicks, context switches).
  const currentKey = columns.find((c) => c.isCurrent)?.key;
  React.useEffect(() => {
    if (!currentKey || !stripRef.current) return;
    const el = stripRef.current.querySelector<HTMLElement>(
      `[data-col-key="${CSS.escape(currentKey)}"]`
    );
    el?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [currentKey]);

  const scrollStrip = (dir: 1 | -1) =>
    stripRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });

  const expandedCol = expandedKey
    ? columns.find((c) => c.key === expandedKey)
    : undefined;

  const patentSection = (col: FeatureColumnData, imgHeight: string) => (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-1.5 px-2.5 py-1.5 bg-[#F8FAFC] border-b border-gray-200">
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] font-interSemiBold tracking-wide uppercase text-[#667085]">
            Patent Figure
          </span>
          {sourceLink(patentLink, "View Patent")}
        </span>
        <button
          type="button"
          onClick={() => onChangePatentImage(col.key)}
          className={actionBtnCls}
          style={actionBtnStyle}
        >
          <CameraIcon size={12} stroke="currentColor" />
          Change Image
        </button>
      </div>
      <div className="bg-[#FAFBFC]">
        {col.patentFigureUrl ? (
          <ZoomableImage
            url={col.patentFigureUrl}
            alt={`Patent figure — ${col.subFeature}`}
            height={imgHeight}
          />
        ) : (
          <NoPreview height={imgHeight} />
        )}
      </div>
      {col.figureNumber && (
        <p className="text-center text-[13px] text-gray-600 font-interMedium py-1 border-t border-dashed border-gray-200">
          {col.figureNumber.replace(/_/g, ". ")}
        </p>
      )}
    </div>
  );

  const accusedSection = (col: FeatureColumnData, imgHeight: string) => (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-1.5 px-2.5 py-1.5 bg-[#F8FAFC] border-b border-gray-200">
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] font-interSemiBold tracking-wide uppercase text-[#667085]">
            Accused Instrumentality
          </span>
          {sourceLink(targetLink, "View Target")}
        </span>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => onAnnotate(col.key)}
            className={`edit-image-button ${actionBtnCls}`}
            style={actionBtnStyle}
          >
            <PencilIcon />
            Annotation
            <span className="loading-spinner"></span>
          </button>
          <button
            type="button"
            onClick={() => onReanalyze(col.key)}
            className={actionBtnCls}
            style={actionBtnStyle}
          >
            <RefreshIcon />
            ReAnalyze
          </button>
        </div>
      </div>
      <div className="bg-[#FAFBFC]">
        {col.accusedImageUrl ? (
          <ZoomableImage
            url={col.accusedImageUrl}
            alt={`Accused instrumentality — ${col.subFeature}`}
            height={imgHeight}
          />
        ) : (
          <NoPreview height={imgHeight} />
        )}
      </div>
    </div>
  );

  const infoSections = (col: FeatureColumnData, includeChartAndAnalysis: boolean) => (
    <>
      {/* Figure legend */}
      {col.figureLegend && col.figureLegend.length > 0 && (
        <div className="mt-2.5 border border-gray-200 rounded-lg bg-[#FAFBFC] px-3 py-2">
          <p className="text-[10px] font-interSemiBold uppercase tracking-wide text-[#667085] mb-1">
            Figure Legend
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
            {col.figureLegend.map((legend, index) => {
              const key = Object.keys(legend)[0];
              return (
                <div key={index} className="flex items-start text-[12px]">
                  <span className="font-interMedium text-gray-800 mr-1.5">{key}.</span>
                  <span className="text-gray-600">{legend[key]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Single-feature claim chart (expanded modal only — the grid shows one
          common table for the whole element below the columns) */}
      {includeChartAndAnalysis && col.chartHtml && (
        <div
          className="feature-chart mt-2.5"
          dangerouslySetInnerHTML={{ __html: col.chartHtml }}
        />
      )}

      {/* Verdict */}
      {col.matchPercentage !== undefined && col.matchPercentage !== null && (
        <div className="mt-2.5 border border-gray-200 rounded-lg overflow-hidden text-[12px]">
          <div className="flex justify-between items-center px-2.5 py-1.5">
            <span className="text-[#667085] font-interMedium">Match percentage</span>
            <span
              className={`text-[10.5px] font-interSemiBold px-2 py-[1px] rounded-full border ${
                col.isMatched
                  ? "text-green-700 bg-green-50 border-green-200"
                  : "text-red-700 bg-red-50 border-red-200"
              }`}
            >
              {String(col.matchPercentage)}%
            </span>
          </div>
        </div>
      )}

      {/* Text analysis */}
      {includeChartAndAnalysis && col.analysisHtml && (
        <div className="mt-2.5 border border-gray-200 rounded-lg bg-[#F8FAFC] px-3 py-2">
          <p className="text-[10px] font-interSemiBold uppercase tracking-wide text-[#667085] mb-1">
            Analysis — {col.subFeature}
          </p>
          <div
            className="text-[12.5px] text-[#475569] leading-relaxed"
            dangerouslySetInnerHTML={{ __html: col.analysisHtml }}
          />
        </div>
      )}

    </>
  );

  return (
    <div>
      {/* Element clause node */}
      <div className="w-full border border-gray-200 rounded-xl bg-[#F8FAFC] px-6 py-3 text-center">
        <p
          className="text-[13px] text-[#475569]"
          dangerouslySetInnerHTML={{ __html: clauseHtml }}
        />
      </div>

      <div className="text-center text-gray-400 text-lg leading-none my-2">
        ↓
      </div>

      {/* Columns — one horizontal strip, scrollable when features overflow */}
      {strip.overflowing && (
        <div className="flex items-center justify-end gap-2 mb-2">
          <span className="text-[11px] font-interMedium text-[#667085]">
            {n} features — scroll for more
          </span>
          <button
            type="button"
            title="Scroll left"
            onClick={() => scrollStrip(-1)}
            disabled={!strip.canLeft}
            className="p-0.5 rounded-full border border-gray-200 bg-white shadow-sm text-[#475569] hover:bg-gray-50 disabled:opacity-35 disabled:hover:bg-white"
            style={{ cursor: strip.canLeft ? "pointer" : "default" }}
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </button>
          <button
            type="button"
            title="Scroll right"
            onClick={() => scrollStrip(1)}
            disabled={!strip.canRight}
            className="p-0.5 rounded-full border border-gray-200 bg-white shadow-sm text-[#475569] hover:bg-gray-50 disabled:opacity-35 disabled:hover:bg-white"
            style={{ cursor: strip.canRight ? "pointer" : "default" }}
          >
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </button>
        </div>
      )}
      <div className="relative">
        {strip.canLeft && (
          <>
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-white to-transparent z-[5]" />
            <button
              type="button"
              title="Scroll left"
              onClick={() => scrollStrip(-1)}
              className="absolute left-1 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 rounded-full shadow-md p-1 text-[#475569] hover:bg-gray-50"
              style={{ cursor: "pointer" }}
            >
              <ChevronLeftIcon sx={{ fontSize: 22 }} />
            </button>
          </>
        )}
        {strip.canRight && (
          <>
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-white to-transparent z-[5]" />
            <button
              type="button"
              title="Scroll right"
              onClick={() => scrollStrip(1)}
              className="absolute right-1 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 rounded-full shadow-md p-1 text-[#475569] hover:bg-gray-50"
              style={{ cursor: "pointer" }}
            >
              <ChevronRightIcon sx={{ fontSize: 22 }} />
            </button>
          </>
        )}
        <div
          ref={stripRef}
          onScroll={updateStrip}
          className={`flex gap-5 overflow-x-auto snap-x pb-2 [scrollbar-width:thin] ${
            strip.overflowing ? "" : "justify-center"
          }`}
        >
        {columns.map((col) => (
          <div
            key={col.key}
            data-col-key={col.key}
            className={`flex flex-col items-center flex-shrink-0 snap-start ${
              n === 1 ? "w-full" : "w-[330px]"
            }`}
          >
            <button
              type="button"
              onClick={() => onSelectColumn(col.key)}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-[13px] font-interSemiBold transition-all ${
                col.isCurrent
                  ? "border-[#1751D0] text-[#1751D0] bg-[#F5F8FD] shadow-[0_0_0_3px_rgba(23,81,208,0.12)]"
                  : "border-gray-300 text-[#344054] bg-white hover:border-[#1751D0]"
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  col.isMatched ? "bg-green-500" : "bg-red-500"
                }`}
              />
              {col.subFeature}
            </button>
            <div className="text-gray-400 text-lg leading-none my-1.5">↓</div>
            <div
              className={`w-full border-2 rounded-xl bg-white overflow-hidden flex-1 flex flex-col ${
                col.isMatched
                  ? col.isCurrent
                    ? "border-green-600 shadow-[0_0_0_4px_rgba(34,197,94,0.35)]"
                    : "border-green-500 shadow-[0_0_0_2px_rgba(34,197,94,0.22)]"
                  : col.isCurrent
                  ? "border-red-600 shadow-[0_0_0_4px_rgba(239,68,68,0.35)]"
                  : "border-red-500 shadow-[0_0_0_2px_rgba(239,68,68,0.22)]"
              }`}
            >
              {/* Expand to 80% modal */}
              <div className="flex justify-end px-1.5 pt-1.5 -mb-1">
                <button
                  type="button"
                  title="Expand"
                  onClick={() => onToggleExpand?.(col.key)}
                  className="p-1 rounded hover:bg-gray-100 text-[#667085]"
                  style={{ cursor: "pointer" }}
                >
                  <OpenInFullIcon sx={{ fontSize: 15 }} />
                </button>
              </div>

              <div className="mx-3 mt-1">{patentSection(col, "14rem")}</div>

              {/* VS */}
              <div className="flex items-center gap-2.5 mx-4 my-2">
                <span className="flex-1 border-t border-dashed border-gray-200" />
                <span className="text-[10px] font-interSemiBold text-gray-400">VS</span>
                <span className="flex-1 border-t border-dashed border-gray-200" />
              </div>

              <div className="mx-3">{accusedSection(col, "14rem")}</div>

              <div className="mx-3 mb-3">{infoSections(col, false)}</div>
            </div>
          </div>
        ))}
        </div>
      </div>

      {/* Common claim chart — every feature's row pulled from its own item */}
      {commonChartHtml && (
        <div className="mt-7">
          <p className="text-[11px] font-interSemiBold uppercase tracking-wide text-[#667085] mb-1.5">
            Claim Chart — All Features
          </p>
          <div
            className="feature-chart feature-common-chart overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: commonChartHtml }}
          />
        </div>
      )}

      {/* Expanded 80% modal — restore via the icon or a backdrop click */}
      {expandedCol && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: 1200 }}
          onClick={() => onToggleExpand?.(null)}
        >
          <div
            className={`bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden border-2 ${
              expandedCol.isMatched ? "border-green-500" : "border-red-500"
            }`}
            style={{ width: "80vw", height: "80vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
              <span className="inline-flex items-center gap-2 text-[14px] font-interSemiBold text-[#1A202C]">
                <span
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    expandedCol.isMatched ? "bg-green-500" : "bg-red-500"
                  }`}
                />
                {expandedCol.subFeature}
              </span>
              <button
                type="button"
                title="Restore"
                onClick={() => onToggleExpand?.(null)}
                className="p-1 rounded hover:bg-gray-100 text-[#667085]"
                style={{ cursor: "pointer" }}
              >
                <CloseFullscreenIcon sx={{ fontSize: 17 }} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {patentSection(expandedCol, "48vh")}
                {accusedSection(expandedCol, "48vh")}
              </div>
              {infoSections(expandedCol, true)}
            </div>
          </div>
        </div>
      )}

      {/* table styling for the single-feature charts */}
      <style>{`
        .feature-chart table { width: 100%; border-collapse: collapse; font-size: 12px; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
        .feature-chart th { background: #f8fafc; text-align: left; font-size: 10px; letter-spacing: .04em; text-transform: uppercase; color: #667085; padding: 6px 9px; border-bottom: 1px solid #e5e7eb; }
        .feature-chart td { padding: 7px 9px; vertical-align: top; color: #334155; border-bottom: 1px solid #f1f5f9; }
        .feature-chart tr:last-child td { border-bottom: none; }
        .feature-chart tr.pf-current td { background: #F5F8FD; }
        .feature-chart .fig-ref { font-weight: 700; background: #f3f4f6; border: 1px solid #e5e7eb; border-radius: 4px; padding: 0 4px; }
      `}</style>
    </div>
  );
};

export default PerFeatureAnalysisView;
